import { styled } from '@linaria/react';
import { Flex, Group, HoverCard, Switch } from '@mantine/core';
import { useNavigate } from 'react-router';
import type { UserInfo } from '../../api/user-client/user-client.type';
import { useFeatureFlag } from '../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../helpers/feature-flags/feature-flags.type';
import { logout } from '../../helpers/storage/storage';
import { useGlobalStore } from '../../store/global-store/global-store';
import { setSuperuserMode } from '../../store/global-store/global-store.actions';
import { baseBlack, inkLight, redBase, secondaryBase, skyDark, skyLighter } from '../../styles/design-tokens';
import { Avatar } from '../../ui-library/avatar/avatar';
import { Button } from '../../ui-library/button/button';
import { Divider } from '../../ui-library/divider/divider';
import { Icon } from '../../ui-library/icon/icon';
import { Text } from '../../ui-library/typography/typography';

type Props = {
  user: UserInfo | null;
};

export function User({ user }: Props) {
  const superuserMode = useGlobalStore((state) => state.superuserMode);
  const navigate = useNavigate();
  const isSettingsEnabled = useFeatureFlag(FeatureFlag.AgenticProcessByHooks);

  return (
    // Mantine >= 9.7 no longer opens a HoverCard on tap; keep the header menu
    // reachable on touch devices (tablets, touch laptops) as it was before.
    <HoverCard position="bottom-end" events={{ touch: true }}>
      <HoverCard.Target>
        <Avatar radius="xl" data-bf="header-btn-usermenu" />
      </HoverCard.Target>
      {user && (
        <StyledDropdown>
          <Flex direction="column" style={{ padding: '4px 16px', width: 'auto' }}>
            <Text weight="bold" color={baseBlack}>
              {user.name}
            </Text>
            <Text weight="medium" color={skyDark}>
              {user.email}
            </Text>
          </Flex>
          <Divider />
          <LinkButton
            data-bf="usermenu-link-data-management"
            onClick={() => {
              navigate('/application/data-management');
            }}
          >
            <Icon name="account_tree" variant="outlined" size={20} color={secondaryBase} />
            <Text lineHeight="tight" weight="medium">
              Data Management
            </Text>
          </LinkButton>
          <Divider />
          <LinkButton
            data-bf="usermenu-link-calculations"
            onClick={() => {
              navigate('/application/calculations');
            }}
          >
            <Icon name="calculate" size={20} color={secondaryBase} />
            <Text lineHeight="tight" weight="medium">
              Calculations
            </Text>
          </LinkButton>
          <Divider />
          <LinkButton data-bf="usermenu-link-terms" onClick={() => navigate('/application/legal/terms-and-conditions')}>
            <Icon name="article" size={20} color={secondaryBase} />
            <Text lineHeight="tight" weight="medium">
              Terms and Conditions
            </Text>
          </LinkButton>
          <Divider />
          {isSettingsEnabled && (
            <>
              <LinkButton data-bf="usermenu-link-settings" onClick={() => navigate('/application/settings/api-keys')}>
                <Icon name="settings" variant="outlined" size={20} color={secondaryBase} />
                <Text lineHeight="tight" weight="medium">
                  Settings
                </Text>
              </LinkButton>
              <Divider />
            </>
          )}
          {user.is_superuser && (
            <>
              <Group justify="space-between">
                <Text weight="medium" color={redBase}>
                  ENABLE SUPERUSER MODE
                </Text>
                <StyledSwitch
                  aria-label="Enable superuser mode"
                  color={redBase}
                  checked={superuserMode.enabled}
                  onChange={() => {
                    setSuperuserMode({
                      enabled: !superuserMode.enabled,
                      forceRecalculation: false,
                      showModelSelection: false,
                    });
                  }}
                />
              </Group>
              <Divider />
            </>
          )}

          <StyledButton
            data-bf="usermenu-btn-signout"
            variant="outline"
            radius="xl"
            onClick={() => logout()}
            leftSection={<Icon name="exit_to_app" size={16} color={inkLight} style={{ cursor: 'pointer' }} />}
          >
            <Text lineHeight="none" weight="medium">
              Sign Out
            </Text>
          </StyledButton>
        </StyledDropdown>
      )}
    </HoverCard>
  );
}

const StyledDropdown = styled(HoverCard.Dropdown)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  box-shadow: var(--box-shadow-big-soft);
  background-color: var(--base-white);
  border-radius: var(--border-radius-xtiny);
  box-sizing: border-box;
  width: 280px;
  min-width: 280px;
  padding: 24px 16px;
`;

const LinkButton = styled.button`
  display: flex;
  align-items: center;
  text-decoration: none;
  background-color: transparent;
  border: none;
  padding: 16px 32px;
  text-align: left;
  width: 100%;

  &:hover {
    cursor: pointer;
    background-color: ${skyLighter};
  }
`;

const StyledSwitch = styled(Switch)`
  .mantine-Switch-track {
    cursor: pointer;
  }
`;

const StyledButton = styled(Button)`
  width: 100%;
  height: 36px;
  padding: 8px 22px;
`;
